import { computed, Injectable } from '@angular/core';
import {
  ConfigurationService,
  ContextService,
  FeatureService,
  AIFeatures,
  AIUserData,
  AI_CONFIG_DEFAULT_USER_DATA,
  AIAdminData,
  AI_CONFIG_DEFAULT_ADMIN_DATA,
  AiUserDataGroup,
} from '@maximizer/core/shared/data-access';
import {
  ConfigurationSetting,
  ConfigurationSettingCodes,
  Session,
} from '@maximizer/core/shared/domain';
import { map, tap, Observable, catchError, of, switchMap, forkJoin } from 'rxjs';
import { AIStore } from '../state/ai.store';
import {
  CODE_ADMIN,
  DefaultDataScope,
  IQBoostSavedConfigurationAdmin,
  IQBoostSavedConfigurationUser,
  IQDataProcessingFeatures,
  IQDataProcessingFeaturesGroup,
  ViewType,
} from '../classes/iqBoost';
import { 
  createDefaultIqBoostAdminSavedConfiguration, 
  createDefaultIqBoostUserSavedConfiguration, 
  IQBoostViewStore 
} from '../state/iqboost-view.store';

@Injectable()
export class AiFeaturesService {
  enableSetting: ConfigurationSetting | null = null;
  aiUserDataSetting: ConfigurationSetting | null = null;
  aiAdminDataSetting: ConfigurationSetting | null = null;
  iqBoostAdminFilterSetting: ConfigurationSetting | null = null;
  iqBoostUserFilterSetting: ConfigurationSetting | null = null;
  session: Session | null = null;

  readonly enabledSettingCodes: ConfigurationSettingCodes = {
    code3: 'AI',
    code4: 'Enabled',
  };
  readonly aiUserDataSettingsCodes: ConfigurationSettingCodes = {
    code3: 'AI_USER_DATA',
    code4: 'DATA',
  };
  readonly aiAdminDataSettingsCodes: ConfigurationSettingCodes = {
    code3: 'AI_USER_DATA',
    code4: 'DATA',
    code2: CODE_ADMIN,
  };
  readonly featureFlag = 'core-ai-features-enabled';
  readonly moduleId = 'maximizer-ai';

  isAdmin = false;
  isEnabled = false;

  showAISummarization = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-timeline-ai-summarization-shown', false) &&
    this.featureService.isFeatureOn('core-ai-anonymizer-enabled', false) &&
    this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled.summarization &&
    this.aiStore.aiUserDataGroup().admin.aiFeaturesEnabled.summarization
  );

  showAIMeetingPreparation = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-ai-meeting-preparation-summary-shown', false) &&
    this.featureService.isFeatureOn('core-ai-anonymizer-enabled', false) &&
    this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled.meetingPreparation &&
    this.aiStore.aiUserDataGroup().admin.aiFeaturesEnabled.meetingPreparation
  );

  showIQBoost = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-ai-iq-boost-shown', false) &&
    this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled.iqBoost &&
    this.aiStore.aiUserDataGroup().admin.aiFeaturesEnabled.iqBoost
  );

  showIQBoostWidget = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-ai-iq-boost-widget-shown', true) &&
    this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled.iqBoostWidget &&
    this.aiStore.aiUserDataGroup().admin.aiFeaturesEnabled.iqBoostWidget
  );

  isSLE = computed(() =>
    this.featureService.isFeatureOn('tmp-core-ai-iq-boost-is-sle', false),
  );

  constructor(
    private readonly context: ContextService,
    private readonly configurationService: ConfigurationService,
    private readonly featureService: FeatureService,
    private readonly aiStore: AIStore,
    private readonly iqBoostStore: IQBoostViewStore,
  ) {}

  getInstalled(): boolean {
    const module = this.context.modules.find((m) => m.id === this.moduleId);
    return (
      this.featureService.isFeatureOn(this.featureFlag, false) &&
      module?.overrideView?.integrationArgs !== undefined
    );
  }

  getEnabled(): Observable<boolean> {
    return this.configurationService.getSetting(this.enabledSettingCodes).pipe(
      tap((setting) => {
        this.enableSetting = setting;
      }),
      map((setting) => setting?.textValue === 'true'),
    );
  }

  getConfiguration(session: Session): Observable<{ installed: boolean; enabled: boolean; aiUserDataGroup: AiUserDataGroup }> {
    const installed = this.getInstalled();
    this.session = session;
    this.isAdmin = session.user.roles.administrator;
    
    if (!installed) {
      const defaultGroup: AiUserDataGroup = {
        admin: AI_CONFIG_DEFAULT_ADMIN_DATA,
        user: AI_CONFIG_DEFAULT_USER_DATA,
        isAdmin: this.isAdmin
      };
      return of({ installed, enabled: false, aiUserDataGroup: defaultGroup });
    }

    const enabled$ = this.getEnabled();
    const aiUserDataGroup$ = this.getAIUserData(session);

    return forkJoin({
      enabled: enabled$,
      aiUserDataGroup: aiUserDataGroup$,
    }).pipe(
      tap(({ enabled, aiUserDataGroup }) => {
        this.aiStore.setUserDataGroup(aiUserDataGroup);
        this.isEnabled = enabled;
      }),
      map(({ enabled, aiUserDataGroup }) => ({
        installed,
        enabled,
        aiUserDataGroup,
      })),
    );
  }

  getAIUserData(session: Session): Observable<AiUserDataGroup> {
    const adminCodes = { ...this.aiUserDataSettingsCodes, code2: CODE_ADMIN };
    const userCodes = { ...this.aiUserDataSettingsCodes, code2: session.user.id };

    // Always fetch both admin and user data
    return forkJoin({
      admin: this.configurationService.getSetting(adminCodes).pipe(
        map((setting) => {
          this.aiAdminDataSetting = setting;
          return setting?.textValue 
            ? JSON.parse(setting.textValue) as AIAdminData 
            : AI_CONFIG_DEFAULT_ADMIN_DATA;
        }),
        catchError(() => of(AI_CONFIG_DEFAULT_ADMIN_DATA))
      ),
      user: this.configurationService.getSetting(userCodes).pipe(
        map((setting) => {
          this.aiUserDataSetting = setting;
          return setting?.textValue 
            ? JSON.parse(setting.textValue) as AIUserData 
            : AI_CONFIG_DEFAULT_USER_DATA;
        }),
        catchError(() => of(AI_CONFIG_DEFAULT_USER_DATA))
      )
    }).pipe(
      map(({ admin, user }) => ({
        admin,
        user,
        isAdmin: this.isAdmin
      }))
    );
  }

  processFiltersConfiguration(
    session: Session,
    updateViewType = false,
  ): Observable<IQDataProcessingFeaturesGroup> {
    this.iqBoostStore.configurationSettingCodesUser().code2 = session.user.id;

    return forkJoin({
      user: this.configurationService.getSetting(this.iqBoostStore.configurationSettingCodesUser())
        .pipe(
          map((setting) => {
            const configuration: IQBoostSavedConfigurationUser = setting?.textValue
              ? JSON.parse(setting.textValue)
              : createDefaultIqBoostUserSavedConfiguration();
            this.iqBoostStore.setConfigurationSettingUser(setting);
            this.iqBoostStore.setDataScope(configuration.dataScope ?? DefaultDataScope);

            if (updateViewType) {
              this.iqBoostStore.setViewType(configuration.wasFirstTimeOpenedPrivacySecurityCenter
                ? ViewType.Report
                : ViewType.PrivacySecurityCenter
              );
            }

            return configuration;
          }),
        ),
      admin: this.configurationService.getSetting(this.iqBoostStore.configurationSettingCodesAdmin())
        .pipe(
          map((setting) => {
            const configuration: IQBoostSavedConfigurationAdmin = setting?.textValue
              ? JSON.parse(setting.textValue)
              : createDefaultIqBoostAdminSavedConfiguration();
            this.iqBoostStore.setConfigurationSettingAdmin(setting);
            return configuration;
          }),
        ),
    }).pipe(
      catchError(() =>
        of({
          user: createDefaultIqBoostUserSavedConfiguration(),
          admin: createDefaultIqBoostAdminSavedConfiguration(),
        }).pipe(
          tap(() => {
            this.iqBoostStore.setConfigurationSettingUser(null);
            this.iqBoostStore.setConfigurationSettingAdmin(null);
            this.iqBoostStore.setDataScope(DefaultDataScope);
            if (updateViewType) {
              this.iqBoostStore.setViewType(ViewType.PrivacySecurityCenter);
            }
          }),
        ),
      ),
      map(({ user, admin }) => ({
        user,
        admin,
        isAdmin: session.user.roles.administrator,
      })),
      map((dataProcessingFeatureGroup) =>
        this.applyAnonymizerFeatureFlag(dataProcessingFeatureGroup)
      ),
      tap((dataProcessingFeatureGroup) =>
        this.iqBoostStore.setDataProcessingFeatureGroup(dataProcessingFeatureGroup)
      ),
    );
  }

  addCreditsUsed(creditsUsed: number): Observable<boolean> {
    const aiUserDataGroup = this.aiStore.aiUserDataGroup();
    const currentCreditsUsed = aiUserDataGroup.admin.creditsUsed;
    this.aiStore.setCreditsUsed(currentCreditsUsed + creditsUsed);
    return this.saveAdminData();
  }

  setAIFeatures(aiFeatures: AIFeatures): Observable<boolean> {
    this.aiStore.setAiFeaturesEnabled(aiFeatures);
    
    if (this.isAdmin) {
      // For admin, save to both admin and user configurations
      return forkJoin({
        admin: this.saveAdminData(),
        user: this.saveUserData()
      }).pipe(
        map(({ admin, user }) => admin && user)
      );
    } else {
      // For non-admin, save only to user configuration
      return this.saveUserData();
    }
  }

  setEnabled(enabled: boolean): Observable<boolean> {
    const currentSetting = this.enableSetting
      ? of(this.enableSetting)
      : this.configurationService.getSetting(this.enabledSettingCodes);

    return currentSetting.pipe(
      switchMap(() =>
        this.configurationService.updateSetting(
          this.enableSetting?.id ?? null,
          this.enabledSettingCodes,
          enabled.toString(),
        ),
      ),
      tap((setting) => {
        this.enableSetting = setting;
      }),
      catchError(() => of(null)),
      map((setting) => (setting?.id?.length ?? 0) > 0),
    );
  }

  getAdminDataProcessingFeatures(): Observable<IQDataProcessingFeatures> {
    return this.configurationService
      .getSetting(this.iqBoostStore.configurationSettingCodesAdmin())
      .pipe(
        map((setting) => {
          this.iqBoostAdminFilterSetting = setting;
          const configuration: IQBoostSavedConfigurationAdmin = setting?.textValue
            ? JSON.parse(setting.textValue)
            : createDefaultIqBoostAdminSavedConfiguration();
          return configuration.dataProcessingFeatures;
        }),
        map((dataProcessingFeatures) =>
          this.applyAnonymizerFeatureFlagToFeatures(dataProcessingFeatures),
        ),
        catchError(() =>
          of(createDefaultIqBoostAdminSavedConfiguration().dataProcessingFeatures),
        ),
      );
  }

  getAnonymizerConfiguration(): Observable<{
    admin: IQDataProcessingFeatures;
    user: IQDataProcessingFeatures;
  }> {
    return forkJoin({
      admin: this.getAdminDataProcessingFeatures(),
      user: this.configurationService
        .getSetting(this.iqBoostStore.configurationSettingCodesUser())
        .pipe(
          map((setting) => {
            this.iqBoostUserFilterSetting = setting;
            const configuration: IQBoostSavedConfigurationUser = setting?.textValue
              ? JSON.parse(setting.textValue)
              : createDefaultIqBoostUserSavedConfiguration();
            return configuration.dataProcessingFeatures;
          }),
          map((dataProcessingFeatures) =>
            this.applyAnonymizerFeatureFlagToFeatures(dataProcessingFeatures),
          ),
          catchError(() =>
            of(createDefaultIqBoostUserSavedConfiguration().dataProcessingFeatures),
          ),
        ),
    });
  }

  setAnonymizerEnabled(
    anonymized: boolean,
    isAdmin: boolean,
  ): Observable<boolean> {
    const userCodes = this.iqBoostStore.configurationSettingCodesUser();
    const adminCodes = this.iqBoostStore.configurationSettingCodesAdmin();
    const userSetting$ = this.iqBoostUserFilterSetting
      ? of(this.iqBoostUserFilterSetting)
      : this.configurationService.getSetting(userCodes);
    let adminSetting$: Observable<ConfigurationSetting | null>;
    if (isAdmin) {
      adminSetting$ = this.iqBoostAdminFilterSetting
        ? of(this.iqBoostAdminFilterSetting)
        : this.configurationService.getSetting(adminCodes);
    } else {
      adminSetting$ = of(null);
    }

    return forkJoin({ user: userSetting$, admin: adminSetting$ }).pipe(
      switchMap(({ user, admin }) => {
        const userConfiguration = this.parseIqBoostUserConfiguration(
          user?.textValue,
        );
        const userUpdate$ = this.configurationService.updateSetting(
          user?.id ?? null,
          userCodes,
          JSON.stringify({
            ...userConfiguration,
            dataProcessingFeatures: {
              ...userConfiguration.dataProcessingFeatures,
              anonymized,
            },
          }),
        );

        if (!isAdmin || !admin) {
          return userUpdate$.pipe(
            tap((setting) => this.syncAnonymizerStoreAfterSave(setting, null)),
            catchError(() => of(null)),
            map((setting) => (setting?.id?.length ?? 0) > 0),
          );
        }

        const adminConfiguration = this.parseIqBoostAdminConfiguration(
          admin.textValue,
        );

        return forkJoin({
          user: userUpdate$,
          admin: this.configurationService.updateSetting(
            admin.id ?? null,
            adminCodes,
            JSON.stringify({
              dataProcessingFeatures: {
                ...adminConfiguration.dataProcessingFeatures,
                anonymized,
              },
            }),
          ),
        }).pipe(
          tap(({ user: userSetting, admin: adminSetting }) =>
            this.syncAnonymizerStoreAfterSave(userSetting, adminSetting),
          ),
          catchError(() => of({ user: null, admin: null })),
          map(
            ({ user: userSetting, admin: adminSetting }) =>
              (userSetting?.id?.length ?? 0) > 0 &&
              (adminSetting?.id?.length ?? 0) > 0,
          ),
        );
      }),
    );
  }

  private parseIqBoostUserConfiguration(
    textValue?: string | null,
  ): IQBoostSavedConfigurationUser {
    if (!textValue) {
      return createDefaultIqBoostUserSavedConfiguration();
    }

    try {
      return JSON.parse(textValue) as IQBoostSavedConfigurationUser;
    } catch {
      return createDefaultIqBoostUserSavedConfiguration();
    }
  }

  private parseIqBoostAdminConfiguration(
    textValue?: string | null,
  ): IQBoostSavedConfigurationAdmin {
    if (!textValue) {
      return createDefaultIqBoostAdminSavedConfiguration();
    }

    try {
      return JSON.parse(textValue) as IQBoostSavedConfigurationAdmin;
    } catch {
      return createDefaultIqBoostAdminSavedConfiguration();
    }
  }

  private syncAnonymizerStoreAfterSave(
    userSetting: ConfigurationSetting | null,
    adminSetting: ConfigurationSetting | null,
  ): void {
    if (userSetting) {
      this.iqBoostUserFilterSetting = userSetting;
      this.iqBoostStore.setConfigurationSettingUser(userSetting);
      const userConfiguration: IQBoostSavedConfigurationUser = userSetting.textValue
        ? JSON.parse(userSetting.textValue)
        : createDefaultIqBoostUserSavedConfiguration();
      this.iqBoostStore.setDataProcessingFeatureGroup({
        ...this.iqBoostStore.dataProcessingFeatureGroup(),
        user: userConfiguration,
      });
    }

    if (adminSetting) {
      this.iqBoostAdminFilterSetting = adminSetting;
      this.iqBoostStore.setConfigurationSettingAdmin(adminSetting);
      const adminConfiguration: IQBoostSavedConfigurationAdmin = adminSetting.textValue
        ? JSON.parse(adminSetting.textValue)
        : createDefaultIqBoostAdminSavedConfiguration();
      this.iqBoostStore.setDataProcessingFeatureGroup({
        ...this.iqBoostStore.dataProcessingFeatureGroup(),
        admin: adminConfiguration,
      });
    }
  }

  private applyAnonymizerFeatureFlagToFeatures(
    dataProcessingFeatures: IQDataProcessingFeatures,
  ): IQDataProcessingFeatures {
    if (this.featureService.isFeatureOn('core-ai-anonymizer-enabled', true)) {
      return dataProcessingFeatures;
    }

    return { ...dataProcessingFeatures, anonymized: true };
  }

  private saveAdminData(): Observable<boolean> {
    const aiUserDataGroup = this.aiStore.aiUserDataGroup();
    const currentAdminData = {
      creditsUsed: aiUserDataGroup.admin.creditsUsed,
      creditsTotal: aiUserDataGroup.admin.creditsTotal,
      aiFeaturesEnabled: aiUserDataGroup.admin.aiFeaturesEnabled,
    };
    const currentSetting = this.aiAdminDataSetting
      ? of(this.aiAdminDataSetting)
      : this.configurationService.getSetting(this.aiAdminDataSettingsCodes);

    return currentSetting.pipe(
      switchMap((response) =>
        this.configurationService.updateSetting(
          response?.id ?? null,
          this.aiAdminDataSettingsCodes,
          JSON.stringify(currentAdminData),
        ),
      ),
      tap((setting) => this.aiAdminDataSetting = setting),
      catchError(() => of(null)),
      map((setting) => (setting?.id?.length ?? 0) > 0),
    );
  }

  private saveUserData(): Observable<boolean> {
    const aiUserDataGroup = this.aiStore.aiUserDataGroup();
    const currentUserData = {
      aiFeaturesEnabled: aiUserDataGroup.user.aiFeaturesEnabled,
    };
    const settingsCodes = { ...this.aiUserDataSettingsCodes, code2: this.session?.user.id };
    const currentSetting = this.aiUserDataSetting
      ? of(this.aiUserDataSetting)
      : this.configurationService.getSetting(settingsCodes);

    return currentSetting.pipe(
      switchMap((response) =>
        this.configurationService.updateSetting(
          response?.id ?? null,
          settingsCodes,
          JSON.stringify(currentUserData),
        ),
      ),
      tap((setting) => this.aiUserDataSetting = setting),
      catchError(() => of(null)),
      map((setting) => (setting?.id?.length ?? 0) > 0),
    );
  }

  private applyAnonymizerFeatureFlag(
    dataProcessingFeatureGroup: IQDataProcessingFeaturesGroup,
  ): IQDataProcessingFeaturesGroup {
    if (this.featureService.isFeatureOn('core-ai-anonymizer-enabled', true)) {
      return dataProcessingFeatureGroup;
    }

    return {
      ...dataProcessingFeatureGroup,
      user: {
        ...dataProcessingFeatureGroup.user,
        dataProcessingFeatures: {
          ...dataProcessingFeatureGroup.user.dataProcessingFeatures,
          anonymized: true,
        },
      },
      admin: {
        ...dataProcessingFeatureGroup.admin,
        dataProcessingFeatures: {
          ...dataProcessingFeatureGroup.admin.dataProcessingFeatures,
          anonymized: true,
        },
      },
    };
  }
}
